import {
  isLiveSubscriptionStatus,
  isSettlingShopifyBridge,
  LIVE_SUBSCRIPTION_STATUSES,
  type LivePriceDto,
  type StripeSubscriptionStatus,
} from "@intelligems/ig-types";
import {
  formatDate,
  formatShortDate,
  isAgencyProduct,
  isEntitlementGrantingProduct,
  isPauseProduct,
} from "@intelligems/ig-utils";

import { findLapsedFreeTrial } from "./lapsedFreeTrial";

import type { BillingModule, BillingPlan } from "./types";
import type { BillingFrequency } from "../types";
import {
  MODULE_ORDER,
  MODULE_STATIC_CONFIG,
  MONTHLY_EQUIVALENT_PRICE_FREQUENCY,
  PLAN_PROFILE_KEYS,
  PLAN_STATIC_CONFIG,
  type PlanStaticConfig,
  PRODUCT_VERSION_2026,
  SMART_PRICING_UNLIMITED_ID,
  SMART_PRICING_VARIANT_DISPLAY,
  SMART_PRICING_VARIANTS,
  UNLIMITED_ID,
} from "./constants";
import type {
  BillingCatalog,
  BillingCycle,
  BillingSnapshot,
  RawSubscriptionAccess,
} from "./types";

type Subscription = RawSubscriptionAccess["subscriptions"]["active"][number];

const MONTHLY_PERIOD = { interval: "month", intervalCount: 1 };

const toBillingFrequency = ({
  interval,
  intervalCount,
}: {
  interval: string;
  intervalCount: number;
}): BillingFrequency => {
  if (interval === "year") {
    return "annually";
  }
  if (intervalCount === 1) {
    return "monthly";
  }
  return intervalCount === 3 ? "quarterly" : `every ${intervalCount} months`;
};

export const currentLiveSubscription = (
  snapshot: BillingSnapshot
): Subscription | undefined => {
  const live = snapshot.subscriptionAccessData.subscriptions.active
    .filter(({ status }) => isLiveSubscriptionStatus(status))
    .sort((a, b) =>
      a.stripeSubscriptionId.localeCompare(b.stripeSubscriptionId)
    );
  const byStatusThenId = LIVE_SUBSCRIPTION_STATUSES.flatMap((status) =>
    live.filter((subscription) => subscription.status === status)
  );

  return byStatusThenId.find(({ cancelAt }) => !cancelAt) ?? byStatusThenId[0];
};

/**
 * A subscription's status as the merchant experiences it, which is not always
 * the string Stripe reports.
 *
 * A Shopify bridge in a settling status (`past_due` on a renewal, `incomplete`
 * on the first invoice) waits on our own out-of-band settlement, not on the
 * merchant: Shopify is the payment authority and the backend keeps the org's
 * entitlements (`isSettlingShopifyBridge`). Reading Stripe's string literally
 * puts a "Past due" chip on a merchant who has already paid — and one they
 * cannot act on, since the recovery CTA is Stripe-only — or, for `incomplete`,
 * offers a free trial to a merchant already paying for the product.
 *
 */
export const merchantFacingStatus = (
  subscription: Subscription | undefined
): StripeSubscriptionStatus | undefined =>
  subscription &&
  isSettlingShopifyBridge({
    status: subscription.status,
    billingProvider: subscription.billingProvider,
  })
    ? "active"
    : (subscription?.status as StripeSubscriptionStatus | undefined);

export const subscriptionCadence = (
  subscription: Pick<Subscription, "interval">
): BillingCycle => (subscription.interval === "year" ? "year" : "month");

export const currentCadence = (snapshot: BillingSnapshot): BillingCycle => {
  const subscription = currentLiveSubscription(snapshot);

  return subscription ? subscriptionCadence(subscription) : "month";
};

const isPastThreeMonthMinimum = (startDate: number | undefined): boolean => {
  if (startDate == null) {
    return true;
  }

  const threeMonthsAfterStart = new Date(startDate * 1_000);
  threeMonthsAfterStart.setMonth(threeMonthsAfterStart.getMonth() + 3);

  return threeMonthsAfterStart <= new Date();
};

const isLivePriceNotArchived = (livePrice: LivePriceDto): boolean =>
  !livePrice.product.entitlementProfileKey?.endsWith("-ARCHIVED");

// The two cadences Billing offers. Stripe also carries multi-month rows
// (quarterly, semi-annual) that a brand can be billed on but that the merchant
// is never asked to pick. Those rows must still reach the ownership checks —
// they are how a quarterly brand's modules are recognised — so this predicate
// guards only what gets offered or priced, never what counts as held.
export const isOfferableCadence = ({
  interval,
  intervalCount,
}: LivePriceDto): boolean =>
  intervalCount === 1 && (interval === "month" || interval === "year");

export const matchLivePriceByIds = (
  priceIds: string[],
  livePrices: LivePriceDto[],
  preferredInterval?: string
): LivePriceDto | undefined => {
  const candidates = livePrices.filter(
    (livePrice) =>
      isLivePriceNotArchived(livePrice) &&
      isOfferableCadence(livePrice) &&
      (livePrice.priceIds.some((id) => priceIds.includes(id)) ||
        priceIds.includes(livePrice.id))
  );

  if (!preferredInterval) {
    return (
      candidates.find(({ interval }) => interval === "month") ??
      candidates.find(({ interval }) => interval === "year")
    );
  }

  const direct = candidates.find(
    ({ interval }) => interval === preferredInterval
  );
  if (direct) {
    return direct;
  }

  // The id matched a row of another cadence; resolve the same product's price
  // for the requested cadence. A missing cadence resolves to nothing — never
  // substitute a price the merchant would not actually be billed.
  const productCandidate = candidates[0];
  if (!productCandidate) {
    return undefined;
  }

  return livePrices.find(
    (livePrice) =>
      livePrice.product.id === productCandidate.product.id &&
      isLivePriceNotArchived(livePrice) &&
      isOfferableCadence(livePrice) &&
      livePrice.interval === preferredInterval
  );
};

export const matchLivePriceByName = (
  productName: string,
  livePrices: LivePriceDto[],
  preferredInterval?: string
): LivePriceDto | undefined => {
  const candidates = livePrices.filter(
    (livePrice) =>
      livePrice.product.name === productName &&
      isLivePriceNotArchived(livePrice) &&
      isOfferableCadence(livePrice)
  );

  if (!preferredInterval) {
    return (
      candidates.find(({ interval }) => interval === "month") ??
      candidates.find(({ interval }) => interval === "year")
    );
  }

  // A missing cadence resolves to nothing — never substitute the monthly row.
  return candidates.find(({ interval }) => interval === preferredInterval);
};

const buildPlan = (
  profileKey: string,
  staticConfig: PlanStaticConfig,
  priceIds: string[],
  livePrices: LivePriceDto[],
  preferredInterval: string | undefined,
  subscription: Subscription | undefined,
  isCurrent: boolean,
  priceOverride?: number
): BillingPlan => {
  const livePrice = matchLivePriceByIds(
    priceIds,
    livePrices,
    preferredInterval
  );
  const { cancelAt, pauseAt, periodEnd, startDate, trialEnd } =
    subscription ?? {};
  const previousPrice = livePrice?.comparePrice ?? undefined;
  const { chips: staticChips, ...staticPresentation } = staticConfig;
  const chips =
    staticChips ??
    (previousPrice != null
      ? [
          {
            label: "savings",
            variant: "info" as const,
            slot: "savings" as const,
          },
        ]
      : undefined);

  return {
    ...staticPresentation,
    id: profileKey,
    type: "plan",
    features: livePrice?.features ?? [],
    currentPrice:
      priceOverride ??
      livePrice?.monthlyEquivalentPrice ??
      livePrice?.price ??
      0,
    intervalPrice: livePrice?.price,
    previousPrice,
    currentPriceFrequency: MONTHLY_EQUIVALENT_PRICE_FREQUENCY,
    previousPriceFrequency:
      previousPrice != null ? MONTHLY_EQUIVALENT_PRICE_FREQUENCY : undefined,
    billingFrequency: toBillingFrequency(
      livePrice ??
        subscription ?? {
          interval: preferredInterval ?? "month",
          intervalCount: 1,
        }
    ),
    renewalDate: periodEnd ? formatDate(periodEnd) : undefined,
    cancelledAt:
      cancelAt && cancelAt * 1_000 > Date.now()
        ? formatShortDate(cancelAt)
        : undefined,
    pauseAt:
      pauseAt && pauseAt * 1_000 > Date.now() ? formatDate(pauseAt) : undefined,
    status: merchantFacingStatus(subscription),
    trialEndAt: trialEnd ?? undefined,
    isPastThreeMonthMinimum: isPastThreeMonthMinimum(startDate),
    isCurrent,
    chips,
    startDate,
    stripePriceId: livePrice?.id,
  };
};

const formatPlans = (
  snapshot: BillingSnapshot,
  cadence: BillingCycle
): BillingPlan[] => {
  const { subscriptionAccessData, livePrices, actualPriceByProductId } =
    snapshot;
  const { access, subscriptions, upgradeDowngradePaths } =
    subscriptionAccessData;
  const products = access.products.filter(
    (product) => !product.entitlementProfileKey?.endsWith("-ARCHIVED")
  );
  const currentSubscription = currentLiveSubscription(snapshot);
  const hasTrialingSubscription = subscriptions.active.some(
    ({ status }) => status === "trialing"
  );
  // The backend is the source of truth for whether this org belongs on the
  // modular billing surface (regime, live modular subs, or a fresh/churned org
  // defaulting forward). A non-modular org is routed to the legacy panel and
  // never reaches this catalog.
  const uses2026Billing = subscriptionAccessData.org.isModular;
  const plans: BillingPlan[] = [];

  for (const product of products.filter(
    ({ entitlementProfileKey }) =>
      entitlementProfileKey != null &&
      PLAN_PROFILE_KEYS.has(entitlementProfileKey)
  )) {
    const profileKey = product.entitlementProfileKey!;
    const productSubscription =
      subscriptions.active.find(
        ({ cancelAt, priceIds }) =>
          !cancelAt && product.priceIds.some((id) => priceIds.includes(id))
      ) ??
      subscriptions.active.find(({ priceIds }) =>
        product.priceIds.some((id) => priceIds.includes(id))
      ) ??
      currentSubscription;
    const isCurrent = !!currentSubscription?.priceIds.some((id) =>
      product.priceIds.includes(id)
    );

    plans.push(
      buildPlan(
        profileKey,
        PLAN_STATIC_CONFIG[profileKey],
        product.priceIds,
        livePrices,
        cadence,
        productSubscription,
        isCurrent,
        isCurrent
          ? actualPriceByProductId.get(product.stripeProductId)
          : undefined
      )
    );
  }

  if (!plans.some(({ id }) => id === UNLIMITED_ID)) {
    const { eligible, deferred, blocked } = upgradeDowngradePaths;
    const upgradeToUnlimited = [...eligible, ...deferred, ...blocked].find(
      ({ changeType }) => changeType === "upgrade_to_blue"
    );
    if (upgradeToUnlimited) {
      const livePrice = matchLivePriceByIds(
        upgradeToUnlimited.target.priceIds,
        livePrices,
        cadence
      );
      if (livePrice) {
        plans.push(
          buildPlan(
            UNLIMITED_ID,
            PLAN_STATIC_CONFIG[UNLIMITED_ID],
            upgradeToUnlimited.target.priceIds,
            livePrices,
            cadence,
            undefined,
            false
          )
        );
      }
    }
  }

  if (
    uses2026Billing &&
    (subscriptions.active.length === 0 || hasTrialingSubscription) &&
    plans.length === 0
  ) {
    const unlimitedPrices = livePrices.filter(
      (livePrice) =>
        livePrice.product.version === PRODUCT_VERSION_2026 &&
        isLivePriceNotArchived(livePrice) &&
        isOfferableCadence(livePrice) &&
        (livePrice.product.entitlementProfileKey === UNLIMITED_ID ||
          livePrice.product.name === PLAN_STATIC_CONFIG[UNLIMITED_ID].name)
    );

    if (unlimitedPrices.length > 0) {
      plans.push(
        buildPlan(
          UNLIMITED_ID,
          PLAN_STATIC_CONFIG[UNLIMITED_ID],
          unlimitedPrices.flatMap(({ priceIds }) => priceIds),
          livePrices,
          cadence,
          undefined,
          false
        )
      );
    }
  }

  return plans;
};

type ResolveModuleLivePriceInput = {
  livePrices: LivePriceDto[];
  cadence: BillingCycle;
  activeProductPriceIds?: string[];
  discoverablePriceId?: string;
  tierUpgradePriceId?: string;
  name: string;
  stripeProductNames?: string[];
};

const resolveModuleLivePrice = ({
  livePrices,
  cadence,
  activeProductPriceIds,
  discoverablePriceId,
  tierUpgradePriceId,
  name,
  stripeProductNames,
}: ResolveModuleLivePriceInput): LivePriceDto | undefined =>
  (activeProductPriceIds
    ? matchLivePriceByIds(activeProductPriceIds, livePrices, cadence)
    : undefined) ??
  (tierUpgradePriceId
    ? matchLivePriceByIds([tierUpgradePriceId], livePrices, cadence)
    : undefined) ??
  (discoverablePriceId
    ? matchLivePriceByIds([discoverablePriceId], livePrices, cadence)
    : undefined) ??
  matchLivePriceByName(name, livePrices, cadence) ??
  stripeProductNames
    ?.map((alternativeName) =>
      matchLivePriceByName(alternativeName, livePrices, cadence)
    )
    .find((price) => price != null);

const PLAN_NAMES: ReadonlySet<string> = new Set(
  Object.values(PLAN_STATIC_CONFIG).map(({ name }) => name)
);

const STATIC_KEY_BY_NAME: ReadonlyMap<string, string> = new Map(
  Object.entries(MODULE_STATIC_CONFIG).flatMap(([key, config]) => [
    [config.name, key] as const,
    ...(config.stripeProductNames ?? []).map(
      (alternativeName) => [alternativeName, key] as const
    ),
  ])
);

const VARIANT_TO_PRIMARY_KEY: ReadonlyMap<string, string> = new Map(
  Object.entries(MODULE_STATIC_CONFIG).flatMap(([key, config]) =>
    (config.variantProductKeys ?? []).map(
      (variantKey) => [variantKey, key] as const
    )
  )
);

export const moduleKeyForLivePrice = (livePrice: LivePriceDto): string => {
  const entitlementKey = livePrice.product.entitlementProfileKey;

  return (
    (entitlementKey ? VARIANT_TO_PRIMARY_KEY.get(entitlementKey) : undefined) ??
    STATIC_KEY_BY_NAME.get(livePrice.product.name) ??
    entitlementKey ??
    livePrice.product.name
  );
};

export const isModularModulePrice = (livePrice: LivePriceDto): boolean => {
  const entitlementKey = livePrice.product.entitlementProfileKey;

  return (
    livePrice.product.version === PRODUCT_VERSION_2026 &&
    isLivePriceNotArchived(livePrice) &&
    !isPauseProduct(livePrice.product) &&
    !PLAN_PROFILE_KEYS.has(entitlementKey ?? "") &&
    !PLAN_NAMES.has(livePrice.product.name)
  );
};

/**
 * A response carrying no module price cannot distinguish "this merchant owns nothing"
 * from "the catalog failed to load"
 */
export const hasModularModulePrices = (
  livePrices: readonly LivePriceDto[]
): boolean => livePrices.some(isModularModulePrice);

export const resolveModuleKeysForPriceIds = (
  livePrices: readonly LivePriceDto[],
  priceIds: ReadonlySet<string>
): Set<string> => {
  const keys = new Set<string>();

  for (const livePrice of livePrices) {
    if (
      !isModularModulePrice(livePrice) ||
      ![livePrice.id, ...livePrice.priceIds].some((id) => priceIds.has(id))
    ) {
      continue;
    }

    keys.add(moduleKeyForLivePrice(livePrice));
  }

  return keys;
};

/**
 * A **rider**: a price a subscription legitimately carries that the merchant's Selection
 * cannot express, because the picker never offers it. A Selection-derived Product-set
 * target must carry these forward untouched — the target is declarative, so omitting one
 * cancels a product the merchant pays for
 */
/**
 * The live prices of modules still on a per-module trial that this subscription bills.
 * A Selection deliberately leaves these unselected, so they must be pinned into a
 * declarative Product-set target the same way a rider is — omitting one deletes the
 * item and destroys its Trial Offer mid-trial.
 */
export const trialingPriceIdsOnSubscription = (
  snapshot: BillingSnapshot,
  subscription: Pick<Subscription, "priceIds">
): string[] => {
  const trialingPriceIds = new Set(
    snapshot.subscriptionAccessData.moduleTrials.map(
      (trial) => trial.stripePriceId
    )
  );

  return subscription.priceIds.filter((priceId) =>
    trialingPriceIds.has(priceId)
  );
};

export const riderPriceIdsOnSubscription = (
  snapshot: BillingSnapshot,
  subscription: Pick<Subscription, "priceIds">
): string[] => {
  const riderPriceIds = new Set(
    snapshot.subscriptionAccessData.access.products
      .filter((product) => {
        const classified = {
          name: product.name,
          entitlementProfileKey: product.entitlementProfileKey ?? null,
        };

        return (
          isPauseProduct(product) ||
          isAgencyProduct(classified) ||
          !isEntitlementGrantingProduct(classified)
        );
      })
      .flatMap(({ priceIds }) => priceIds)
  );

  return subscription.priceIds.filter((priceId) => riderPriceIds.has(priceId));
};

/**
 * Every entitlement profile key that identifies one catalog entity — its own id plus the
 * aliases a mid-migration subscription may still be billed under.
 */
export const entityKeys = (
  catalog: BillingCatalog,
  entityId: string
): readonly string[] => {
  const module = catalog.modules.find(({ id }) => id === entityId);

  return [entityId, ...(module?.variantProductKeys ?? [])];
};

export const productsForEntity = (
  snapshot: BillingSnapshot,
  catalog: BillingCatalog,
  entityId: string
): RawSubscriptionAccess["access"]["products"] => {
  const keys = new Set(entityKeys(catalog, entityId));

  return snapshot.subscriptionAccessData.access.products.filter(
    ({ entitlementProfileKey }) =>
      entitlementProfileKey != null && keys.has(entitlementProfileKey)
  );
};

/**
 * The prices this subscription bills for one catalog entity.
 */
export const entityPriceIdsOnSubscription = ({
  catalog,
  entityId,
  snapshot,
  subscription,
}: {
  catalog: BillingCatalog;
  entityId: string;
  snapshot: BillingSnapshot;
  subscription: Pick<Subscription, "priceIds">;
}): string[] => {
  const productPriceIds = new Set(
    productsForEntity(snapshot, catalog, entityId).flatMap(
      ({ priceIds }) => priceIds
    )
  );

  return subscription.priceIds.filter((id) => productPriceIds.has(id));
};

/**
 * The prices a subscription is billed on that no Selection this catalog can express would
 * carry — neither a module, nor a plan, nor a rider.
 *
 * The routine case is an `-ARCHIVED` entitlement key
 */
export const unaccountedSubscriptionPriceIds = (
  snapshot: BillingSnapshot,
  catalog: BillingCatalog,
  subscription: Pick<Subscription, "priceIds">
): string[] => {
  const accountedPriceIds = new Set([
    ...riderPriceIdsOnSubscription(snapshot, subscription),
    ...[...catalog.plans, ...catalog.modules].flatMap(({ id }) =>
      entityPriceIdsOnSubscription({
        catalog,
        entityId: id,
        snapshot,
        subscription,
      })
    ),
  ]);

  return subscription.priceIds.filter(
    (priceId) => !accountedPriceIds.has(priceId)
  );
};

const formatModules = (
  snapshot: BillingSnapshot,
  cadence: BillingCycle
): BillingModule[] => {
  const { subscriptionAccessData, actualPriceByProductId } = snapshot;
  const { access, subscriptions, upgradeDowngradePaths, moduleTrials } =
    subscriptionAccessData;
  // The pause retention product ships in the same 2026 catalog as the modules,
  // but it is an internal swap target for pausing a plan — never something a
  // merchant subscribes to or discovers from Billing.
  const livePrices = snapshot.livePrices.filter(
    ({ product }) => !isPauseProduct(product)
  );
  // Ownership reads `livePrices`, which spans every cadence a brand may be
  // billed on; discovery and pricing read the offerable subset.
  const offerableLivePrices = livePrices.filter(isOfferableCadence);
  const allPaths = [
    ...upgradeDowngradePaths.blocked,
    ...upgradeDowngradePaths.deferred,
    ...upgradeDowngradePaths.eligible,
  ];
  const products = access.products.filter(
    (product) =>
      !product.entitlementProfileKey?.endsWith("-ARCHIVED") &&
      !isPauseProduct(product)
  );
  const isUnlimitedPlan = products.some(
    ({ entitlementProfileKey }) => entitlementProfileKey === UNLIMITED_ID
  );
  const currentSubscription = currentLiveSubscription(snapshot);
  const activeSubscriptionPriceIds = new Set(
    subscriptions.active
      .filter(({ status }) => isLiveSubscriptionStatus(status))
      .flatMap(({ priceIds }) => priceIds)
  );
  const activeProductByKey = new Map(
    products
      .filter(({ entitlementProfileKey }) => entitlementProfileKey != null)
      .map((product) => [product.entitlementProfileKey!, product])
  );
  const planNames = PLAN_NAMES;
  const staticKeyByName = STATIC_KEY_BY_NAME;
  const variantToPrimaryKey = VARIANT_TO_PRIMARY_KEY;

  const subscribedModuleKeys = resolveModuleKeysForPriceIds(
    livePrices,
    activeSubscriptionPriceIds
  );
  const ownedModuleKeys = new Map<string, string>();
  for (const product of products.filter(
    (candidate) =>
      candidate.entitlementProfileKey != null &&
      !PLAN_PROFILE_KEYS.has(candidate.entitlementProfileKey) &&
      !planNames.has(candidate.name) &&
      livePrices.some(
        (livePrice) =>
          livePrice.product.version === PRODUCT_VERSION_2026 &&
          livePrice.priceIds.some((id) => candidate.priceIds.includes(id))
      )
  )) {
    const original = product.entitlementProfileKey!;
    const normalized =
      variantToPrimaryKey.get(original) ??
      staticKeyByName.get(product.name) ??
      original;
    ownedModuleKeys.set(normalized, original);
  }

  const discoverableModules = new Map<
    string,
    { name: string; priceId: string }
  >();
  for (const path of allPaths.filter(
    ({ changeType }) => changeType === "add_module"
  )) {
    const addedPriceId = path.id.replace("add_module:", "");
    const livePrice = offerableLivePrices.find(
      (price) =>
        price.priceIds.includes(addedPriceId) || price.id === addedPriceId
    );
    if (
      !livePrice ||
      livePrice.product.entitlementProfileKey?.endsWith("-ARCHIVED") ||
      planNames.has(livePrice.product.name)
    ) {
      continue;
    }
    const key = moduleKeyForLivePrice(livePrice);
    if (!ownedModuleKeys.has(key)) {
      discoverableModules.set(key, {
        name: livePrice.product.name,
        priceId: addedPriceId,
      });
    }
  }

  if (
    subscriptionAccessData.org.isModular &&
    ownedModuleKeys.size === 0 &&
    discoverableModules.size === 0
  ) {
    for (const livePrice of offerableLivePrices) {
      if (!isModularModulePrice(livePrice)) {
        continue;
      }

      const key = moduleKeyForLivePrice(livePrice);

      if (!discoverableModules.has(key)) {
        discoverableModules.set(key, {
          name: livePrice.product.name,
          priceId: livePrice.id,
        });
      }
    }
  }

  const tierUpgradePriceIdByKey = new Map<string, string>();
  for (const path of upgradeDowngradePaths.eligible.filter(
    ({ changeType }) => changeType === "add_module"
  )) {
    const addedPriceId = path.id.replace("add_module:", "");
    const livePrice = offerableLivePrices.find(
      (price) =>
        price.priceIds.includes(addedPriceId) || price.id === addedPriceId
    );
    if (
      !livePrice ||
      livePrice.product.entitlementProfileKey?.endsWith("-ARCHIVED") ||
      planNames.has(livePrice.product.name)
    ) {
      continue;
    }
    const key = moduleKeyForLivePrice(livePrice);
    if (ownedModuleKeys.has(key)) {
      tierUpgradePriceIdByKey.set(key, addedPriceId);
    }
  }

  const lapsedTrialModuleKeys =
    ownedModuleKeys.size === 0 && subscribedModuleKeys.size === 0
      ? resolveModuleKeysForPriceIds(
          livePrices,
          new Set(findLapsedFreeTrial(subscriptions.historical)?.priceIds ?? [])
        )
      : new Set<string>();

  const moduleKeys = new Set([
    ...ownedModuleKeys.keys(),
    ...subscribedModuleKeys,
    ...lapsedTrialModuleKeys,
    ...discoverableModules.keys(),
  ]);

  return [...moduleKeys].map((profileKey): BillingModule => {
    const originalKey = ownedModuleKeys.get(profileKey) ?? profileKey;
    const staticConfig = MODULE_STATIC_CONFIG[profileKey];
    const {
      commitment,
      name: staticName,
      description: staticDescription,
      chips: staticChips,
      icon,
      variantProductKeys = [],
      stripeProductNames,
    } = staticConfig ?? {};
    const discoverableInfo = discoverableModules.get(profileKey);
    const apiProduct =
      activeProductByKey.get(originalKey) ?? activeProductByKey.get(profileKey);
    const name =
      staticName ?? apiProduct?.name ?? discoverableInfo?.name ?? profileKey;
    const activeProduct =
      activeProductByKey.get(originalKey) ??
      activeProductByKey.get(profileKey) ??
      variantProductKeys
        .map((key) => activeProductByKey.get(key))
        .find((product) => product != null);

    const isActive =
      subscribedModuleKeys.has(profileKey) ||
      isUnlimitedPlan ||
      [originalKey, profileKey, ...variantProductKeys].some(
        (key) =>
          activeProductByKey
            .get(key)
            ?.priceIds.some((id) => activeSubscriptionPriceIds.has(id)) ?? false
      );
    const livePrice = resolveModuleLivePrice({
      livePrices: offerableLivePrices,
      cadence,
      activeProductPriceIds: activeProduct?.priceIds,
      discoverablePriceId: !activeProduct
        ? discoverableInfo?.priceId
        : undefined,
      tierUpgradePriceId: isActive
        ? tierUpgradePriceIdByKey.get(profileKey)
        : undefined,
      name,
      stripeProductNames,
    });
    const actualPrice =
      isActive && activeProduct
        ? actualPriceByProductId.get(activeProduct.stripeProductId)
        : undefined;
    const moduleSubscription = activeProduct
      ? (subscriptions.active.find(({ priceIds }) =>
          activeProduct.priceIds.some((id) => priceIds.includes(id))
        ) ?? null)
      : null;
    const isCommitmentModule = commitment > 0;
    const scheduledRemoval = activeProduct
      ? (subscriptionAccessData.scheduledModuleRemovals ?? []).find((removal) =>
          removal.priceIds.some((id) => activeProduct.priceIds.includes(id))
        )
      : undefined;
    const removalEffectiveAtSec = scheduledRemoval
      ? Date.parse(scheduledRemoval.effectiveAt) / 1_000
      : undefined;
    // A Module Trial can run while other Modules are paid; the subscription's Free Trial deadline does not apply.
    const moduleTrial = activeProduct
      ? moduleTrials.find(
          (trial) => trial.stripeProductId === activeProduct.stripeProductId
        )
      : undefined;

    const trialConverts =
      moduleTrial !== undefined && moduleTrial.endBehavior !== "cancels";
    const trialUnconfirmable =
      moduleTrial?.endBehavior === "converts_cancellable";

    return {
      commitment,
      id: profileKey,
      type: "module",
      chips: staticChips,
      icon,
      name,
      description: staticDescription ?? "",
      isActive,
      cancelledAt:
        isCommitmentModule &&
        moduleSubscription?.cancelAt &&
        moduleSubscription.cancelAt * 1_000 > Date.now()
          ? formatShortDate(moduleSubscription.cancelAt)
          : undefined,
      pauseAt:
        isCommitmentModule &&
        moduleSubscription?.pauseAt &&
        moduleSubscription.pauseAt * 1_000 > Date.now()
          ? formatDate(moduleSubscription.pauseAt)
          : undefined,
      scheduledRemoval:
        scheduledRemoval &&
        removalEffectiveAtSec !== undefined &&
        removalEffectiveAtSec * 1_000 > Date.now()
          ? {
              effectiveAt: removalEffectiveAtSec,
              commandId: scheduledRemoval.commandId,
            }
          : undefined,
      isPastThreeMonthMinimum: isCommitmentModule
        ? isPastThreeMonthMinimum(moduleSubscription?.startDate)
        : undefined,
      startDate: moduleSubscription?.startDate,
      trialEndAt: moduleTrial?.endDate,
      trialConverts,
      trialUnconfirmable,
      stripePriceId: livePrice?.id,
      features: livePrice?.features ?? [],
      currentPrice:
        actualPrice ??
        livePrice?.monthlyEquivalentPrice ??
        livePrice?.price ??
        0,
      intervalPrice: livePrice?.price,
      previousPrice: livePrice?.comparePrice ?? undefined,
      currentPriceFrequency: MONTHLY_EQUIVALENT_PRICE_FREQUENCY,
      previousPriceFrequency:
        livePrice?.comparePrice != null
          ? MONTHLY_EQUIVALENT_PRICE_FREQUENCY
          : undefined,
      variantProductKeys,
      stripeProductNames,
    };
  });
};

const buildCurrentPlanFromModules = (
  subscription: Subscription | undefined,
  activeModules: BillingModule[],
  isPaused: boolean
): BillingPlan => {
  const earliestStart = activeModules.reduce<number | undefined>(
    (minimum, module) =>
      module.startDate !== undefined &&
      (minimum === undefined || module.startDate < minimum)
        ? module.startDate
        : minimum,
    undefined
  );
  const { periodEnd, periodStart, cancelAt, trialEnd } = subscription ?? {};

  return {
    id: "active-modules",
    type: "plan",
    name: "Current Plan",
    description: "",
    features: [],
    commitment: 0,
    currentPrice: 0,
    currentPriceFrequency: MONTHLY_EQUIVALENT_PRICE_FREQUENCY,
    billingFrequency: toBillingFrequency(subscription ?? MONTHLY_PERIOD),
    renewalDate: periodEnd ? formatDate(periodEnd) : undefined,
    cancelledAt:
      cancelAt && cancelAt * 1_000 > Date.now()
        ? formatShortDate(cancelAt)
        : undefined,
    // A paused org's subscription carries only the pause retention price, so
    // Stripe still reports it as active. The owned pause product is the signal.
    status: isPaused ? "paused" : merchantFacingStatus(subscription),
    trialEndAt: trialEnd ?? undefined,
    startDate: earliestStart ?? periodStart,
  };
};

export const sortByCanonicalModuleOrder = <T extends { id: string }>(
  entities: T[]
): T[] => {
  const rank = new Map(MODULE_ORDER.map((id, index) => [id, index]));
  return [...entities].sort(
    (left, right) =>
      (rank.get(left.id) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(right.id) ?? Number.MAX_SAFE_INTEGER)
  );
};

/**
 * One Smart Pricing entry, at the tier the merchant actually gets.
 *
 * The two Smart Pricing products are tiers of one entitlement, not two modules, and the
 * Unlimited tier supersedes Limited — which is the tier the Unlimited bundle grants.
 * Every other layer already reads them this way: the Selection keeps a single variant and
 * the quote prices a single variant, so a list carrying both is the outlier.
 *
 * Both tiers reach an Unlimited org's catalog, but neither through ownership. They arrive
 * as `add_module` paths, because the backend offers one for every catalog module whose
 * price is not already a line item on the subscription, and `formatModules` turns those
 * paths into modules that the Unlimited blanket then reports active.
 *
 * Preferring the Unlimited tier only when it is present is a defensive fallback rather
 * than a shape production sends: it keeps a catalog that somehow surfaced only the
 * Limited tier from losing Smart Pricing altogether.
 */
export const collapseSmartPricingVariants = <T extends { id: string }>(
  entities: readonly T[]
): T[] => {
  const grantedId = entities.some(({ id }) => id === SMART_PRICING_UNLIMITED_ID)
    ? SMART_PRICING_UNLIMITED_ID
    : entities.find(({ id }) => SMART_PRICING_VARIANTS.has(id))?.id;

  return entities.filter(
    ({ id }) => !SMART_PRICING_VARIANTS.has(id) || id === grantedId
  );
};

/**
 * The active modules as the merchant is shown and reported as holding them.
 *
 * `activeModules` is the entitlement set, and the layers that plan changes need it whole:
 * a Selection-derived target, the gate model and the plan-total math all read both Smart
 * Pricing tiers off it. Only what is *presented* — the row list and the analytics that
 * describe it — collapses them, and only under Unlimited, the one plan that bundles the
 * two tiers into a single entitlement. On any other plan a subscription carrying both is
 * carrying two billed line items, and each is a row the merchant can act on in its own
 * right.
 */
export const presentedActiveModules = (
  catalog: BillingCatalog
): BillingModule[] =>
  catalog.currentPlan?.id === UNLIMITED_ID
    ? collapseSmartPricingVariants(catalog.activeModules)
    : [...catalog.activeModules];

export const isPausedAccount = (snapshot: BillingSnapshot): boolean =>
  snapshot.subscriptionAccessData.access.products.some(isPauseProduct);

/**
 * The day a lapsed Free Trial stopped being available to the merchant.
 *
 * The trial *deadline* is what the merchant was told, but only when the trial ran
 * its course. `findLapsedFreeTrial` also recognises a trial cancelled early —
 * `endedAt` before `trialEnd` — and there the deadline never arrived: quoting it
 * would label the trial as ended on a date that can still be in the future. So the
 * earlier of the two is the only honest answer.
 */
const endedTrialDate = ({
  endedAt,
  trialEnd,
}: Pick<Subscription, "endedAt" | "trialEnd">): number | undefined => {
  if (trialEnd == null) {
    return endedAt ?? undefined;
  }

  return endedAt == null ? trialEnd : Math.min(trialEnd, endedAt);
};

const buildEndedTrialPlan = (
  subscription: Subscription,
  endedTrialModules: BillingModule[]
): BillingPlan => ({
  id: "ended-trial-plan",
  type: "plan",
  name: "Current Plan",
  description: "",
  features: [],
  commitment: 0,
  // Left at 0 on purpose: the price shown for a lapsed trial is the cost to RESUME
  // it, which depends on the modules it carried and their bundle discount. The
  // projector computes that from the ended module set and passes it in, so a figure
  // here would only be a second, staler source of truth.
  currentPrice: 0,
  currentPriceFrequency: MONTHLY_EQUIVALENT_PRICE_FREQUENCY,
  billingFrequency: toBillingFrequency(subscription),
  status: subscription.status as StripeSubscriptionStatus | undefined,
  trialEndAt: endedTrialDate(subscription),
  startDate:
    endedTrialModules.reduce<number | undefined>(
      (minimum, module) =>
        module.startDate !== undefined &&
        (minimum === undefined || module.startDate < minimum)
          ? module.startDate
          : minimum,
      undefined
    ) ?? subscription.periodStart,
});

export const buildBillingCatalog = (
  snapshot: BillingSnapshot,
  cadence: BillingCycle
): BillingCatalog => {
  const plans = formatPlans(snapshot, cadence);
  const currentPlanIds = new Set(
    plans.filter(({ isCurrent }) => isCurrent).map(({ id }) => id)
  );
  const modules = sortByCanonicalModuleOrder(
    formatModules(snapshot, cadence).filter(({ id }) => !currentPlanIds.has(id))
  );
  const activeModules = modules.filter(({ isActive }) => isActive);
  const isSubscribedToUnlimited =
    snapshot.subscriptionAccessData.access.entitlementProfileKeys.includes(
      UNLIMITED_ID
    );
  const currentPlanFromApi = isSubscribedToUnlimited
    ? plans.find(({ id }) => id === UNLIMITED_ID)
    : undefined;
  const subscription =
    currentLiveSubscription(snapshot) ??
    snapshot.subscriptionAccessData.subscriptions.active[0];
  const isPaused = isPausedAccount(snapshot);
  const currentPlan =
    currentPlanFromApi ??
    (activeModules.length > 0 || isPaused
      ? buildCurrentPlanFromModules(subscription, activeModules, isPaused)
      : undefined);
  // Only surfaced when nothing live replaced it: once the merchant starts a new
  // plan, the live plan is the truth and the lapsed trial is just history.
  const lapsedTrial = currentPlan
    ? null
    : findLapsedFreeTrial(
        snapshot.subscriptionAccessData.subscriptions.historical
      );
  const endedTrialModuleKeys = lapsedTrial
    ? resolveModuleKeysForPriceIds(
        snapshot.livePrices.filter(({ product }) => !isPauseProduct(product)),
        new Set(lapsedTrial.priceIds)
      )
    : new Set<string>();
  const endedTrialModules = lapsedTrial
    ? modules
        .filter(({ id }) => endedTrialModuleKeys.has(id))
        .map((module) => ({
          ...module,
          // Same date the plan row shows, for the same reason — see
          // `endedTrialDate`. Stripe settling a beat after the deadline keeps the
          // deadline; a trial cancelled early takes the earlier day it really ended.
          trialEndAt: endedTrialDate(lapsedTrial),
        }))
    : [];
  const endedTrialPlan = lapsedTrial
    ? buildEndedTrialPlan(lapsedTrial, endedTrialModules)
    : undefined;
  const smartPricingVariants = modules.some(({ id }) =>
    SMART_PRICING_VARIANTS.has(id)
  )
    ? [...SMART_PRICING_VARIANTS].map((variantId) => {
        const module = modules.find(({ id }) => id === variantId);
        const display = SMART_PRICING_VARIANT_DISPLAY[variantId];
        return {
          id: variantId,
          name: display?.name ?? variantId,
          description: display?.description ?? "",
          price: module?.currentPrice,
          intervalPrice: module?.intervalPrice,
        };
      })
    : [];

  return {
    cadence,
    plans,
    modules,
    currentPlan,
    activeModules,
    ...(endedTrialPlan ? { endedTrialPlan } : {}),
    endedTrialModules,
    smartPricingVariants,
    hasActiveCommitment:
      (currentPlan?.commitment ?? 0) > 0 ||
      activeModules.some(({ commitment }) => commitment > 0),
  };
};

export const entityFor = (
  catalog: BillingCatalog,
  entityId: string
): BillingModule | BillingPlan | undefined =>
  [
    ...(catalog.currentPlan ? [catalog.currentPlan] : []),
    ...catalog.plans,
    ...catalog.modules,
  ].find(({ id }) => id === entityId);

export const subscriptionPeriodEndFor = (
  snapshot: BillingSnapshot,
  catalog: BillingCatalog,
  entityId: string,
  nowSec: number
): number => {
  const entity = entityFor(catalog, entityId);
  const keys = new Set([
    entityId,
    ...(entity?.type === "module" ? (entity.variantProductKeys ?? []) : []),
  ]);
  const product = snapshot.subscriptionAccessData.access.products.find(
    ({ entitlementProfileKey }) =>
      entitlementProfileKey !== null && keys.has(entitlementProfileKey)
  );
  const subscription =
    snapshot.subscriptionAccessData.subscriptions.active.find(
      ({ priceIds, productIds }) =>
        product !== undefined &&
        (productIds.includes(product.stripeProductId) ||
          product.priceIds.some((priceId) => priceIds.includes(priceId)))
    );
  const primarySubscription =
    currentLiveSubscription(snapshot) ??
    snapshot.subscriptionAccessData.subscriptions.active[0];

  return subscription?.periodEnd ?? primarySubscription?.periodEnd ?? nowSec;
};
